import {AxiosError} from 'axios';
import Button from 'components/Button';
import FormResult from 'components/Form/FormResult';
import InputText from 'components/Form/InputText';
import Layout from 'components/Layout';
import {FC, useEffect, useRef, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {c, t} from 'ttag';
import {FormFields} from 'types/utils';
import {setUrl} from 'utils/request';
import {onApiError} from 'utils/request-axios/utils';

import useGetDXCodeInfo from './services';

const GROUP_COUNT = 3;
const DIGITS_PER_GROUP = 4;

type CodeFormProps = {
    clientCode?: string;
};

type FormData = FormFields<{
    codeGroups: string[];
}>;

const CodeForm: FC<CodeFormProps> = ({clientCode}) => {
    const [codeGroups, setCodeGroups] = useState<string[]>(() => {
        if (clientCode) {
            const groups = [];
            for (let i = 0; i < GROUP_COUNT; i++) {
                groups.push(
                    clientCode.substring(
                        i * DIGITS_PER_GROUP,
                        (i + 1) * DIGITS_PER_GROUP,
                    ),
                );
            }
            return groups;
        }
        return Array(GROUP_COUNT).fill('');
    });
    const {mutateAsync} = useGetDXCodeInfo();
    const methods = useForm<FormData>();
    const {handleSubmit} = methods;
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [, setActiveGroupIndex] = useState<number>(0);
    const containerRef = useRef<HTMLDivElement>(null);

    const isFormValid = () => {
        return (
            codeGroups.every((group) => group.length === DIGITS_PER_GROUP) &&
            codeGroups.join('').length === DIGITS_PER_GROUP * GROUP_COUNT
        );
    };

    const onSubmit = async ({codeGroups}: FormData) => {
        setIsSubmitting(true);
        const fullCode = codeGroups.join('');
        mutateAsync({code: fullCode})
            .then(() => {
                setUrl(`/dx/${fullCode}`);
            })
            .catch((error: AxiosError) => {
                onApiError(methods)(error);
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    const focusInput = (index: number) => {
        if (containerRef.current) {
            const inputs = containerRef.current.querySelectorAll('input');
            if (inputs[index]) {
                inputs[index].focus();
            }
        }
    };

    const onCodeChange = (newCodeGroups: string[]) => {
        if (isFormValid()) {
            methods.setValue('codeGroups', newCodeGroups);
        }
    };

    const handleChange = (
        groupIndex: number,
        e: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const value = e.target.value;

        // Only allow digits
        if (!/^\d*$/.test(value)) {
            return;
        }

        const newCodeGroups = [...codeGroups];
        newCodeGroups[groupIndex] = value.slice(0, DIGITS_PER_GROUP);
        setCodeGroups(newCodeGroups);

        // Auto-focus next input when this one is filled
        if (value.length === DIGITS_PER_GROUP && groupIndex < GROUP_COUNT - 1) {
            setActiveGroupIndex(groupIndex + 1);
            focusInput(groupIndex + 1);
        }
    };

    const handleKeyDown = (
        groupIndex: number,
        e: React.KeyboardEvent<HTMLInputElement>,
    ) => {
        // Handle backspace on empty input to go back to previous group
        if (
            (e.key === 'Backspace' || e.key === 'ArrowLeft') &&
            !codeGroups[groupIndex] &&
            groupIndex > 0
        ) {
            e.preventDefault();
            setActiveGroupIndex(groupIndex - 1);
            focusInput(groupIndex - 1);
        } else if (e.key === 'ArrowRight' && groupIndex < GROUP_COUNT - 1) {
            e.preventDefault();
            setActiveGroupIndex(groupIndex + 1);
            focusInput(groupIndex + 1);
        }
    };

    const handlePaste = (
        groupIndex: number,
        e: React.ClipboardEvent<HTMLInputElement>,
    ) => {
        e.preventDefault();
        const pastedData = e.clipboardData
            .getData('text')
            .trim()
            .replace(/-/g, '');

        // Only allow pasting digits
        if (!/^\d+$/.test(pastedData)) {
            return;
        }

        const newCodeGroups = [...codeGroups];

        // Handle different paste scenarios
        if (pastedData.length >= DIGITS_PER_GROUP * GROUP_COUNT) {
            // If pasting the entire code or more
            for (let i = 0; i < GROUP_COUNT; i++) {
                newCodeGroups[i] = pastedData.substring(
                    i * DIGITS_PER_GROUP,
                    (i + 1) * DIGITS_PER_GROUP,
                );
            }
            setActiveGroupIndex(GROUP_COUNT - 1);
            focusInput(GROUP_COUNT - 1);
        } else {
            // Distribute pasted digits across remaining groups
            let remainingDigits = pastedData;

            for (
                let i = groupIndex;
                i < GROUP_COUNT && remainingDigits.length > 0;
                i++
            ) {
                const currentGroupSpace =
                    DIGITS_PER_GROUP - (newCodeGroups[i]?.length || 0);
                const digitsToFill = remainingDigits.slice(
                    0,
                    currentGroupSpace,
                );

                newCodeGroups[i] = (newCodeGroups[i] || '') + digitsToFill;
                remainingDigits = remainingDigits.slice(digitsToFill.length);

                if (i === GROUP_COUNT - 1 || remainingDigits.length === 0) {
                    setActiveGroupIndex(i);
                    focusInput(i);
                }
            }
        }

        setCodeGroups(newCodeGroups);
    };

    useEffect(() => {
        // Focus on the first input on init if no client code
        if (!clientCode) {
            focusInput(0);
        }
    }, []);

    useEffect(() => {
        onCodeChange(codeGroups);
    }, [codeGroups]);

    return (
        <FormProvider {...methods}>
            <h1 className="mt-2">{t`Welcome to Email Address Registration`}</h1>
            <p className="my-2 text-base">
                {t`Please enter your unique 12-digit registration code to continue.`}
            </p>

            <form>
                <Layout.Stacked className="hide-required">
                    <div className="mt-4 flex flex-col items-center">
                        <div>
                            <span className="mb-1 ml-px flex w-full flex-initial items-center text-sm font-semibold text-light-secondary">
                                {t`12-digit registration code`}
                            </span>
                            <div className="flex flex-col items-center sm:flex-row">
                                <div className="flex gap-2" ref={containerRef}>
                                    {Array.from(
                                        {length: GROUP_COUNT},
                                        (_, index) => (
                                            <>
                                                {index !== 0 &&
                                                    index !== GROUP_COUNT && (
                                                        <span className="flex h-auto items-center justify-center">
                                                            -
                                                        </span>
                                                    )}
                                                <div
                                                    className="sm:w-36"
                                                    key={index}
                                                >
                                                    <InputText
                                                        autoComplete="off"
                                                        classNameInput="h-12 px-0 py-1 text-center text-2xl"
                                                        disabled={isSubmitting}
                                                        inputMode="numeric"
                                                        isCompact={true}
                                                        name={`verification-code-group-${index}`}
                                                        onChange={(
                                                            e: React.ChangeEvent<HTMLInputElement>,
                                                        ) =>
                                                            handleChange(
                                                                index,
                                                                e,
                                                            )
                                                        }
                                                        onFocus={() =>
                                                            setActiveGroupIndex(
                                                                index,
                                                            )
                                                        }
                                                        onKeyDown={(
                                                            e: React.KeyboardEvent<HTMLInputElement>,
                                                        ) =>
                                                            handleKeyDown(
                                                                index,
                                                                e,
                                                            )
                                                        }
                                                        onPaste={(
                                                            e: React.ClipboardEvent<HTMLInputElement>,
                                                        ) =>
                                                            handlePaste(
                                                                index,
                                                                e,
                                                            )
                                                        }
                                                        value={
                                                            codeGroups[index]
                                                        }
                                                    />
                                                </div>
                                            </>
                                        ),
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </Layout.Stacked>

                <div className="flex flex-row justify-end gap-4">
                    <Button
                        className="mt-4"
                        disabled={!isFormValid() || isSubmitting}
                        id="submit-code"
                        isLoading={isSubmitting}
                        kind="primary"
                        onClick={handleSubmit(onSubmit)}
                        type="submit"
                    >
                        {c('dx-helper').t`Next`}
                    </Button>
                </div>
            </form>
            <FormResult className="mt-4" />
        </FormProvider>
    );
};

export default CodeForm;
